import React from "react";
import { Cpu, Sparkles } from "lucide-react";

/**
 * States, in each section, whether that section sends text to a model.
 *
 * Most of NUMA runs on arithmetic — references, structure, readability,
 * versions, export — but nothing on screen said so, and people reasonably
 * assume that an app with an assistant in it is an assistant throughout. That
 * assumption matters here: it decides whether an unpublished paper leaves the
 * machine, and whether the user is spending money.
 *
 * Deliberately a permanent line in the page header rather than a dialog: it has
 * to be readable at a glance, every time, without being dismissed once and
 * never seen again.
 */

export const AI_MODES = {
  /** Never contacts a model. */
  none: {
    icon: Cpu,
    label: "Non usa l'AI",
    detail: "Elaborazione algoritmica: nulla di ciò che scrivi lascia il tuo dispositivo.",
    className: "border-line bg-surface-2 text-ink-muted",
    iconClass: "text-ink-muted",
  },
  /** Every action here goes to the connected model. */
  full: {
    icon: Sparkles,
    label: "Usa l'AI",
    detail: "Il testo di questa sezione viene inviato al modello che hai collegato.",
    className: "border-assistant/30 bg-assistant-soft text-assistant",
    iconClass: "text-assistant",
  },
  /** Works without a model; some optional actions use one. */
  optional: {
    icon: Sparkles,
    label: "Usa l'AI solo su richiesta",
    detail: "Funziona senza modello. Le singole funzioni assistite sono indicate.",
    className: "border-line bg-surface-2 text-ink-muted",
    iconClass: "text-assistant",
  },
};

export default function AiBadge({ mode = "none", detail, className = "" }) {
  const meta = AI_MODES[mode] || AI_MODES.none;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`ai-badge-${mode}`}
      className={`inline-flex items-start gap-2 rounded-md border px-2.5 py-1.5 ${meta.className} ${className}`}
    >
      <Icon className={`mt-px h-3.5 w-3.5 shrink-0 ${meta.iconClass}`} aria-hidden="true" />
      <span className="min-w-0">
        <span className="block text-[12.5px] font-medium leading-tight">{meta.label}</span>
        <span className="mt-0.5 block text-2xs leading-snug opacity-90">
          {detail || meta.detail}
        </span>
      </span>
    </span>
  );
}
